'use client';

import type { Column } from '@tanstack/react-table';
import { CalendarIcon, XCircle } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { DateRange } from 'react-day-picker';
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
} from 'date-fns';

// Types for date filter structure
export type DateFilterCondition = 'is-equal-date' | 'is-between-date' | 'is-after-date' | 'is-before-date';

export interface DateFilterValue {
  condition: DateFilterCondition;
  value: string[];
}

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { formatDate } from '@/lib/format';

type DateSelection = Date[] | DateRange;

function getIsDateRange(value: DateSelection): value is DateRange {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function parseAsDate(timestamp: number | string | undefined): Date | undefined {
  if (!timestamp) return undefined;

  // Handle ISO date strings directly
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    return !Number.isNaN(date.getTime()) ? date : undefined;
  }

  // Handle numeric timestamps
  const date = new Date(timestamp);
  return !Number.isNaN(date.getTime()) ? date : undefined;
}

function parseColumnFilterValue(value: unknown) {
  if (value === null || value === undefined) {
    return [];
  }

  // Handle new filter value structure: { condition: string, value: string[] }
  if (typeof value === 'object' && 'value' in value) {
    const { value: dateValues } = value as DateFilterValue;
    return dateValues || [];
  }

  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'number' || typeof item === 'string') {
        return item;
      }
      return undefined;
    });
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return [value];
  }

  return [];
}

const setFrom = (value: Date) => {
  const from = startOfDay(value);
  return from.toISOString();
};

const setTo = (value: Date) => {
  const to = endOfDay(value);
  return to.toISOString();
};

interface DataTableDateFilterProps<TData> {
  column: Column<TData, unknown>;
  title?: string;
  multiple?: boolean;
}

export function DataTableDateFilter<TData>({ column, title, multiple }: DataTableDateFilterProps<TData>) {
  const { t } = useTranslation('common');
  const columnFilterValue = column.getFilterValue() as DateFilterValue;

  const selectedDates = React.useMemo<DateSelection>(() => {
    if (!columnFilterValue) {
      return multiple ? { from: undefined, to: undefined } : [];
    }

    if (multiple) {
      const timestamps = parseColumnFilterValue(columnFilterValue?.value);
      return {
        from: parseAsDate(timestamps[0]),
        to: parseAsDate(timestamps[1]),
      };
    }

    const timestamps = parseColumnFilterValue(columnFilterValue);
    const date = parseAsDate(timestamps[0]);
    return date ? [date] : [];
  }, [columnFilterValue, multiple]);

  const onSelect = React.useCallback(
    (date: Date | DateRange | undefined) => {
      if (!date) {
        column.setFilterValue(undefined);
        return;
      }

      if (multiple && !('getTime' in date)) {
        const from = date.from || new Date();
        const to = date.to;
        if (to) {
          column.setFilterValue({
            condition: 'is-between-date',
            value: [setFrom(from), setTo(to)],
          });
        } else {
          column.setFilterValue({ condition: 'is-equal-date', value: [setFrom(from), setTo(from)] });
        }
      } else if (!multiple && 'getTime' in date) {
        column.setFilterValue({ condition: 'is-equal-date', value: [setFrom(date)] });
      }
    },
    [column, multiple],
  );

  const onReset = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      column.setFilterValue(undefined);
    },
    [column],
  );

  const hasValue = React.useMemo(() => {
    if (multiple) {
      if (!getIsDateRange(selectedDates)) return false;
      return selectedDates.from || selectedDates.to;
    }
    if (!Array.isArray(selectedDates)) return false;
    return selectedDates.length > 0;
  }, [multiple, selectedDates]);

  const formatDateRange = React.useCallback((range: DateRange) => {
    if (!range.from && !range.to) return '';
    if (range.from && range.to) {
      return `${formatDate(range.from)} - ${formatDate(range.to)}`;
    }

    return formatDate(range.from ?? range.to ?? new Date());
  }, []);

  const label = React.useMemo(() => {
    if (multiple) {
      if (!getIsDateRange(selectedDates)) return null;

      const hasSelectedDates = selectedDates.from || selectedDates.to;
      const dateText = hasSelectedDates ? formatDateRange(selectedDates) : t('dataTable.selectDateRange');

      return (
        <span className="flex items-center gap-2">
          <span>{title}</span>
          {hasSelectedDates && (
            <>
              <Separator orientation="vertical" className="mx-0.5 data-[orientation=vertical]:h-4" />
              <span>{dateText}</span>
            </>
          )}
        </span>
      );
    }

    if (getIsDateRange(selectedDates)) return null;

    const hasSelectedDate = selectedDates.length > 0;
    const dateText = hasSelectedDate ? formatDate(selectedDates[0]) : t('dataTable.selectDate');

    return (
      <span className="flex items-center gap-2">
        <span>{title}</span>
        {hasSelectedDate && (
          <>
            <Separator orientation="vertical" className="mx-0.5 data-[orientation=vertical]:h-4" />
            <span>{dateText}</span>
          </>
        )}
      </span>
    );
  }, [selectedDates, multiple, formatDateRange, title, t]);

  // Date range presets
  const presets = React.useMemo(() => {
    const today = new Date();

    return [
      {
        label: t('dataTable.presets.today'),
        getValue: () => ({
          from: startOfDay(today),
          to: endOfDay(today),
        }),
      },
      {
        label: t('dataTable.presets.yesterday'),
        getValue: () => {
          const yesterday = subDays(today, 1);
          return {
            from: startOfDay(yesterday),
            to: endOfDay(yesterday),
          };
        },
      },
      {
        label: t('dataTable.presets.last7Days'),
        getValue: () => ({
          from: startOfDay(subDays(today, 6)),
          to: endOfDay(today),
        }),
      },
      {
        label: t('dataTable.presets.lastWeek'),
        getValue: () => {
          const lastWeek = subWeeks(today, 1);
          return {
            from: startOfWeek(lastWeek, { weekStartsOn: 1 }), // Monday
            to: endOfWeek(lastWeek, { weekStartsOn: 1 }), // Sunday
          };
        },
      },
      {
        label: t('dataTable.presets.last30Days'),
        getValue: () => ({
          from: startOfDay(subDays(today, 29)),
          to: endOfDay(today),
        }),
      },
      {
        label: t('dataTable.presets.thisMonth'),
        getValue: () => ({
          from: startOfMonth(today),
          to: endOfMonth(today),
        }),
      },
      {
        label: t('dataTable.presets.lastMonth'),
        getValue: () => {
          const lastMonth = subMonths(today, 1);
          return {
            from: startOfMonth(lastMonth),
            to: endOfMonth(lastMonth),
          };
        },
      },
      {
        label: t('dataTable.presets.thisYear'),
        getValue: () => ({
          from: startOfYear(today),
          to: endOfYear(today),
        }),
      },
    ];
  }, [t]);

  const handlePresetClick = React.useCallback(
    (getValue: () => DateRange) => {
      const range = getValue();
      if (multiple) {
        onSelect(range);
      }
    },
    [multiple, onSelect],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="border-dashed">
          {hasValue ? (
            <div
              role="button"
              aria-label={t('dataTable.clearFilter', { title: title ?? '' })}
              tabIndex={0}
              onClick={onReset}
              className="focus-visible:ring-ring rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1"
            >
              <XCircle />
            </div>
          ) : (
            <CalendarIcon />
          )}
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {multiple ? (
          <div className="flex">
            {/* Presets sidebar */}
            <div className="flex flex-col gap-1 border-r p-3">
              <div className="text-muted-foreground mb-2 text-xs font-medium">{t('dataTable.quickPresets')}</div>
              {presets.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  className="justify-start text-xs font-normal"
                  onClick={() => handlePresetClick(preset.getValue)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            {/* Calendar */}
            <Calendar
              initialFocus
              mode="range"
              selected={getIsDateRange(selectedDates) ? selectedDates : { from: undefined, to: undefined }}
              onSelect={onSelect}
              weekStartsOn={1}
            />
          </div>
        ) : (
          <Calendar
            initialFocus
            mode="single"
            selected={!getIsDateRange(selectedDates) ? selectedDates[0] : undefined}
            onSelect={onSelect}
            weekStartsOn={1}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
